import React, { useEffect, useState } from 'react';
import {
  Box,
  HeadingText,
  GDSStyleObject,
  StackLayout,
  ParagraphText,
  Radio,
  Fieldset,
  FormField,
  TextInput,
  Select,
} from '@leagueplatform/genesis-core';
import { IS_POLICY_HOLDER_PATIENT } from '../../../../../types/online-forms.types';
import {
  isEmpty,
  isNoSpace,
  isNotEmpty,
  isValidZipCodeLength,
  stateOption,
  updateFormData,
} from '../../../helpers';

export function PatientInformation(props: any) {
  const {
    errorHandling,
    nextButtonClicked,
    profileResponseData,
    isValidateForm,
    footerToBottom,
  } = props;

  const [policyHolderRelationship] = useState(
    profileResponseData?.demographics?.relationship,
  );
  const [isPolicyHolderPatient, setIsPolicyHolderPatient] = useState('');
  const [form, setForm] = useState({
    isPolicyHolderPatient: '',
    patientRelationship: '',
    patientName: '',
    patientAddressLine1: '',
    patientAddressLine2: '',
    patientCity: '',
    patientState: '',
    patientZipCode: '',
    patientDateOfBirth: '',
    patientGender: '',
  });
  const [planDetailsData, setPlanDetailsData] = useState<any>([]);
  const [isPolicyHolderError, setIsPolicyHolderError] = useState(false);
  const [zipCodeError, setZipCodeError] = useState<string>('');

  const [errors, setErrors] = useState({
    patientRelationship: false,
    patientName: false,
    patientAddressLine1: false,
    patientCity: false,
    patientState: false,
    patientZipCode: false,
    patientDateOfBirth: false,
    patientGender: false,
  });

  const formField =
    isEmpty(form.patientRelationship) ||
    isEmpty(form.patientName) ||
    isEmpty(form.patientAddressLine1) ||
    isEmpty(form.patientCity) ||
    isEmpty(form.patientState) ||
    isEmpty(form.patientZipCode) ||
    isValidZipCodeLength(form.patientZipCode) ||
    isEmpty(form.patientGender);

  const isValid =
    isPolicyHolderPatient === 'YES' ||
    (isPolicyHolderPatient === 'NO' &&
      isNotEmpty(form.patientRelationship) &&
      isNotEmpty(form.patientName) &&
      isNotEmpty(form.patientAddressLine1) &&
      isNotEmpty(form.patientCity) &&
      isNotEmpty(form.patientState) &&
      isNotEmpty(form.patientZipCode) &&
      !isValidZipCodeLength(form.patientZipCode) &&
      isNotEmpty(form.patientGender));

  useEffect(() => {
    footerToBottom(false);
  }, [footerToBottom, isPolicyHolderPatient]);

  useEffect(() => {
    if (isPolicyHolderPatient === 'YES') {
      errorHandling(true);
    } else if (isPolicyHolderPatient === '') {
      errorHandling(false);
    } else if (formField || errors.patientZipCode) {
      errorHandling(false);

      if (isEmpty(form.patientZipCode)) {
        setZipCodeError('Zip code is required.');
      } else if (isValidZipCodeLength(form.patientZipCode)) {
        setZipCodeError('Enter a valid zip code.');
      } else {
        setZipCodeError('');
      }
    } else {
      errorHandling(true);
    }
  }, [
    formField,
    errorHandling,
    form,
    isPolicyHolderPatient,
    errors.patientZipCode,
  ]);

  useEffect(() => {
    if (nextButtonClicked) {
      if (!isValid) {
        setErrors({
          patientRelationship: isEmpty(form.patientRelationship),
          patientName: isEmpty(form.patientName),
          patientAddressLine1: isEmpty(form.patientAddressLine1),
          patientCity: isEmpty(form.patientCity),
          patientState: isEmpty(form.patientState),
          patientZipCode:
            isEmpty(form.patientZipCode) ||
            isValidZipCodeLength(form.patientZipCode),
          patientDateOfBirth: isEmpty(form.patientDateOfBirth),
          patientGender: isEmpty(form.patientGender),
        });
        setIsPolicyHolderError(isEmpty(isPolicyHolderPatient));
      }
    }
  }, [nextButtonClicked, isValid, form, isPolicyHolderPatient]);

  useEffect(() => {
    const pInfo = JSON.parse(sessionStorage.getItem('formData') || '{}');
    if (pInfo) {
      setForm({
        isPolicyHolderPatient: pInfo?.isPolicyHolderPatient,
        patientRelationship: pInfo?.patientRelationship,
        patientName: pInfo?.patientName,
        patientAddressLine1: pInfo?.patientAddressLine1,
        patientAddressLine2: pInfo?.patientAddressLine2,
        patientCity: pInfo?.patientCity,
        patientState: pInfo?.patientState,
        patientZipCode: pInfo?.patientZipCode,
        patientDateOfBirth: pInfo?.patientDateOfBirth,
        patientGender: pInfo?.patientGender,
      });
      setPlanDetailsData(pInfo?.dependents);
      setIsPolicyHolderPatient(pInfo?.isPolicyHolderPatient);
    }
  }, []);

  const updateErrorState = (value: any, id: string) => {
    if (id === 'patientZipCode') {
      if (isEmpty(value)) {
        setZipCodeError('Zip code is required.');
        setErrors((prevState) => ({
          ...prevState,
          patientZipCode: true,
        }));
      } else if (isValidZipCodeLength(value)) {
        setZipCodeError('Enter a valid zip code.');
        setErrors((prevState) => ({
          ...prevState,
          patientZipCode: true,
        }));
      } else {
        setZipCodeError('');
        setErrors((prevState) => ({
          ...prevState,
          patientZipCode: false,
        }));
      }
    } else if (
      id === 'isPolicyHolderPatient-1' ||
      id === 'isPolicyHolderPatient-2'
    ) {
      setErrors((prevState) => ({
        ...prevState,
        patientRelationship: false,
        patientName: false,
        patientAddressLine1: false,
        patientCity: false,
        patientState: false,
        patientZipCode: false,
        patientDateOfBirth: false,
        patientGender: false,
      }));
    } else if (id === 'patientName') {
      setErrors((prevState) => ({
        ...prevState,
        [id]: isEmpty(value),
        patientAddressLine1: false,
        patientCity: false,
        patientState: false,
        patientZipCode: false,
        patientDateOfBirth: false,
        patientGender: false,
      }));
    } else if (id === 'patientRelationship') {
      setErrors((prevState) => ({
        ...prevState,
        [id]: isEmpty(value),
      }));
    } else {
      const nextErrorState = {
        ...errors,
        [id]: isEmpty(value),
      };
      setErrors(nextErrorState);
    }
  };

  const onUpdateField = (event: any) => {
    const { value, id } = event.target;
    if (id === 'patientRelationship') {
      const newFormState = {
        ...form,
        [id]: value,
        patientName: '',
        patientAddressLine1: '',
        patientAddressLine2: '',
        patientCity: '',
        patientState: '',
        patientZipCode: '',
        patientDateOfBirth: '',
        patientGender: '',
      };
      setForm(newFormState);
      updateErrorState(value, id);
    } else if (id === 'patientName') {
      const selectedPatientObj = planDetailsData.filter(
        (patient: any) => patient.name === value,
      )[0];
      if (selectedPatientObj) {
        const newFormState = {
          ...form,
          [id]: value,
          patientAddressLine1: selectedPatientObj.addressLine1,
          patientAddressLine2: selectedPatientObj.addressLine2,
          patientCity: selectedPatientObj.city,
          patientState: selectedPatientObj.state,
          patientZipCode: selectedPatientObj.zipCode,
          patientDateOfBirth: selectedPatientObj.dateOfBirth,
          patientGender: selectedPatientObj.gender,
        };
        setForm(newFormState);
        updateErrorState(value, id);
      } else {
        const newFormState = {
          ...form,
          [id]: value,
          patientAddressLine1: '',
          patientAddressLine2: '',
          patientCity: '',
          patientState: '',
          patientZipCode: '',
          patientDateOfBirth: '',
          patientGender: '',
        };
        setForm(newFormState);
        updateErrorState({}, id);
      }
    } else if (id === 'patientGender-1' || id === 'patientGender-2') {
      const nextFormState = {
        ...form,
        patientGender: value,
      };
      setForm(nextFormState);
      updateErrorState(value, 'patientGender');
    } else {
      const nextFormState = {
        ...form,
        [id]: /^\s/.test(value) ? '' : value,
      };
      setForm(nextFormState);
      if (id !== 'patientAddressLine2') {
        updateErrorState(value, id);
      }
    }

    if (id === 'patientCity') {
      setForm((prevState) => ({
        ...prevState,
        patientCity: isNoSpace(value)
          ? ''
          : value?.replace(/[^a-zA-Z ]+/gi, ''),
      }));
    }

    if (id === 'patientZipCode') {
      setForm((prevState) => ({
        ...prevState,
        patientZipCode: isNoSpace(value) ? '' : value?.replace(/[^0-9]+/gi, ''),
      }));
    }
  };

  const updateIsPolicyHolderPatient = (e: any) => {
    const { value, id } = e.target;
    if (value === 'Yes') {
      setIsPolicyHolderPatient('YES');
      setIsPolicyHolderError(false);
      const nextFormState = {
        isPolicyHolderPatient: 'YES',
        patientRelationship: '',
        patientName: '',
        patientAddressLine1: '',
        patientAddressLine2: '',
        patientCity: '',
        patientState: '',
        patientZipCode: '',
        patientDateOfBirth: '',
        patientGender: '',
      };
      isValidateForm(true);
      setForm(nextFormState);
    } else {
      setIsPolicyHolderPatient('NO');
      setIsPolicyHolderError(false);
      const nextFormState = {
        isPolicyHolderPatient: 'NO',
        patientRelationship: '',
        patientName: '',
        patientAddressLine1: '',
        patientAddressLine2: '',
        patientCity: '',
        patientState: '',
        patientZipCode: '',
        patientDateOfBirth: '',
        patientGender: '',
      };
      isValidateForm(false);
      setForm(nextFormState);
    }
    updateErrorState(value, id);
  };

  const patientRelationshipOption = (patientData: any) => {
    const relationshipArrData = patientData
      .map((pat: any) => pat.relationship)
      .filter((rel: any) => rel !== 'Subscriber');
    const filteredRelationshipArrData = [...new Set(relationshipArrData)];
    const patientRelationshipData = filteredRelationshipArrData.map(
      (rel: any) => ({
        value: rel,
        label: rel,
      }),
    );
    return patientRelationshipData;
  };

  const patientNameOption = (patientRelationship: any, patientData: any) => {
    const patientNames = patientData
      .filter((patient: any) => patient.relationship === patientRelationship)
      .map((patient: any) => patient.name);
    const patientNameData = patientNames.map((patient: any) => ({
      value: patient,
      label: patient,
    }));
    return patientNameData;
  };

  const onBlurField = (event: any) => {
    updateErrorState(event.target.value, event.target.id);
  };

  const onKeyPressField = (event: any) => {
    updateErrorState(event.target.value, event.target.id);
  };

  useEffect(() => {
    if (form) {
      updateFormData(form);
    }
  }, [form]);

  return (
    <StackLayout
      css={
        {
          backgroundColor: 'White',
          maxWidth: '800px',
        } as GDSStyleObject
      }
      horizontalAlignment="center"
    >
      <Box
        css={
          {
            display: 'flex',
            flexDirection: 'column',
            padding: '$three $two $two $two',
          } as GDSStyleObject
        }
      >
        <Box
          css={
            {
              display: 'flex',
              flexDirection: 'column',
              padding: '$none $two',
            } as GDSStyleObject
          }
        >
          <HeadingText
            level="1"
            data-testid="patientHeading"
            size={{
              '@initial': 'xxl',
              '@mobile': 'xl',
              '@mobileLandscape': 'xl',
              '@tablet': 'xl',
              '@laptop': 'xxl',
              '@desktop': 'xxl',
            }}
          >
            Member Dental Claim Form
          </HeadingText>
        </Box>
        <Box
          css={
            {
              display: 'flex',
              flexDirection: 'column',
              padding: '$one $two',
              width: '100%',
            } as GDSStyleObject
          }
        >
          <Box
            css={
              {
                display: 'flex',
                flexDirection: 'column',
              } as GDSStyleObject
            }
          >
            <HeadingText
              level="3"
              data-testid="step3"
              size={{
                '@initial': 'xl',
                '@mobile': 'lg',
                '@mobileLandscape': 'lg',
                '@tablet': 'xl',
                '@laptop': 'xl',
                '@desktop': 'xl',
              }}
            >
              Step 3: Patient Information
            </HeadingText>
            <ParagraphText
              css={
                {
                  marginTop: '$one',
                  '@mobile': { width: 'auto' },
                  '@desktop': { width: '600px' },
                  '@laptop': { width: '600px' },
                  '@mobileLandscape': { width: '600px' },
                  '@tablet': { width: '600px' },
                } as GDSStyleObject
              }
            >
              Is the policy holder the patient?
            </ParagraphText>
            <FormField
              id="isPolicyHolderPatient"
              label=""
              statusIconLabel="Error"
              inputStatus={isPolicyHolderError ? 'error' : undefined}
              statusMessage={
                isPolicyHolderError ? 'This selection is required.' : ''
              }
            >
              <Fieldset
                css={
                  {
                    display: 'flex',
                    maxWidth: '436px',
                  } as GDSStyleObject
                }
                legend={undefined}
              >
                <Radio
                  id="isPolicyHolderPatient-1"
                  name="isPolicyHolderPatient"
                  checked={
                    (policyHolderRelationship === 'Subscriber' &&
                      planDetailsData.length === 0) ||
                    isPolicyHolderPatient === 'YES'
                  }
                  disabled={
                    policyHolderRelationship !== 'Subscriber' ||
                    (policyHolderRelationship === 'Subscriber' &&
                      planDetailsData.length === 0)
                  }
                  value={IS_POLICY_HOLDER_PATIENT.YES}
                  label={IS_POLICY_HOLDER_PATIENT.YES}
                  onChange={(e) => updateIsPolicyHolderPatient(e)}
                />
                <Radio
                  id="isPolicyHolderPatient-2"
                  name="isPolicyHolderPatient"
                  checked={
                    policyHolderRelationship !== 'Subscriber' ||
                    isPolicyHolderPatient === 'NO'
                  }
                  disabled={
                    policyHolderRelationship !== 'Subscriber' ||
                    (policyHolderRelationship === 'Subscriber' &&
                      planDetailsData.length === 0)
                  }
                  value={IS_POLICY_HOLDER_PATIENT.NO}
                  label={IS_POLICY_HOLDER_PATIENT.NO}
                  onChange={(e) => updateIsPolicyHolderPatient(e)}
                />
              </Fieldset>
            </FormField>
          </Box>
          {(policyHolderRelationship !== 'Subscriber' ||
            isPolicyHolderPatient === 'NO') && (
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <Box
                css={
                  {
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '$one $none',
                  } as GDSStyleObject
                }
              >
                <FormField
                  id="patientRelationship"
                  label="Patient's Relationship to Policy Holder"
                  statusIconLabel="Error"
                  inputStatus={errors.patientRelationship ? 'error' : undefined}
                  statusMessage={
                    errors.patientRelationship
                      ? "Patient's relationship to policy holder is required."
                      : ''
                  }
                >
                  {policyHolderRelationship !== 'Subscriber' ? (
                    <TextInput value={form.patientRelationship} disabled />
                  ) : (
                    <Select
                      id="patientRelationship"
                      placeholder="Please Select"
                      options={patientRelationshipOption(planDetailsData)}
                      onChange={onUpdateField}
                      value={form.patientRelationship}
                      onBlur={onBlurField}
                      onKeyPress={onKeyPressField}
                    />
                  )}
                </FormField>
              </Box>
              <Box
                css={
                  {
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '$one $none',
                  } as GDSStyleObject
                }
              >
                <FormField
                  id="patientName"
                  label="Patient Name (Last, First, MI, Suffix)"
                  statusIconLabel="Error"
                  inputStatus={errors.patientName ? 'error' : undefined}
                  statusMessage={
                    errors.patientName ? "Patient's name is required." : ''
                  }
                >
                  {policyHolderRelationship !== 'Subscriber' ? (
                    <TextInput value={form.patientName} disabled />
                  ) : (
                    <Select
                      id="patientName"
                      placeholder="Please Select"
                      options={patientNameOption(
                        form.patientRelationship,
                        planDetailsData,
                      )}
                      onChange={onUpdateField}
                      value={form.patientName}
                      disabled={policyHolderRelationship !== 'Subscriber'}
                      onBlur={onBlurField}
                      onKeyPress={onKeyPressField}
                    />
                  )}
                </FormField>
              </Box>
              <Box
                css={
                  {
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '$one $none',
                  } as GDSStyleObject
                }
              >
                <FormField
                  id="patientAddressLine1"
                  label="Patient Address Line 1"
                  statusIconLabel="Error"
                  inputStatus={errors.patientAddressLine1 ? 'error' : undefined}
                  statusMessage={
                    errors.patientAddressLine1
                      ? 'Address line 1 is required.'
                      : ''
                  }
                >
                  <TextInput
                    id="patientAddressLine1"
                    name="patientAddressLine1"
                    value={form.patientAddressLine1}
                    onChange={onUpdateField}
                    maxLength={50}
                    onBlur={onBlurField}
                    onKeyPress={onKeyPressField}
                  />
                </FormField>
              </Box>
              <Box
                css={
                  {
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '$one $none',
                  } as GDSStyleObject
                }
              >
                <FormField
                  id="patientAddressLine2"
                  label="Patient Address Line 2 (Optional)"
                >
                  <TextInput
                    id="patientAddressLine2"
                    name="patientAddressLine2"
                    value={form.patientAddressLine2}
                    onChange={onUpdateField}
                    maxLength={50}
                    onBlur={onBlurField}
                    onKeyPress={onKeyPressField}
                  />
                </FormField>
              </Box>
              <Box
                css={
                  {
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '$one $none',
                  } as GDSStyleObject
                }
              >
                <FormField
                  id="patientCity"
                  label="City"
                  statusIconLabel="Error"
                  inputStatus={errors.patientCity ? 'error' : undefined}
                  statusMessage={errors.patientCity ? 'City is required.' : ''}
                >
                  <TextInput
                    id="patientCity"
                    name="patientCity"
                    value={form.patientCity}
                    onChange={onUpdateField}
                    maxLength={50}
                    onBlur={onBlurField}
                    onKeyPress={onKeyPressField}
                  />
                </FormField>
              </Box>
              <Box
                css={
                  {
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '$one $none',
                  } as GDSStyleObject
                }
              >
                <FormField
                  id="patientState"
                  label="State"
                  statusIconLabel="Error"
                  inputStatus={errors.patientState ? 'error' : undefined}
                  statusMessage={
                    errors.patientState ? 'State is required.' : ''
                  }
                >
                  <Select
                    name="patientState"
                    options={stateOption()}
                    placeholder="Please Select"
                    id="patientState"
                    value={form.patientState}
                    onChange={onUpdateField}
                    onBlur={onBlurField}
                    onKeyPress={onKeyPressField}
                  />
                </FormField>
              </Box>
              <Box
                css={
                  {
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '$one $none',
                  } as GDSStyleObject
                }
              >
                <FormField
                  id="patientZipCode"
                  label="Zip Code"
                  statusIconLabel="Error"
                  inputStatus={errors.patientZipCode ? 'error' : undefined}
                  statusMessage={errors.patientZipCode ? zipCodeError : ''}
                >
                  <TextInput
                    id="patientZipCode"
                    pattern="[0-9]*"
                    name="patientZipCode"
                    value={form.patientZipCode}
                    onChange={onUpdateField}
                    maxLength={5}
                    onBlur={onBlurField}
                    onKeyPress={onKeyPressField}
                  />
                </FormField>
              </Box>
              <Box
                css={
                  {
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '$one $none',
                  } as GDSStyleObject
                }
              >
                <FormField
                  id="patientDateOfBirth"
                  label="Date of Birth (MM/DD/YYYY)"
                  statusIconLabel="Error"
                  inputStatus={errors.patientDateOfBirth ? 'error' : undefined}
                  statusMessage={
                    errors.patientDateOfBirth
                      ? 'Date of birth is required.'
                      : ''
                  }
                >
                  <TextInput disabled value={form.patientDateOfBirth} />
                </FormField>
              </Box>
              <Box
                css={
                  {
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '$one $none',
                  } as GDSStyleObject
                }
              >
                <FormField
                  id="patientGender"
                  label="Gender"
                  statusIconLabel="Error"
                  inputStatus={errors.patientGender ? 'error' : undefined}
                  statusMessage={
                    errors.patientGender ? 'Gender is required.' : ''
                  }
                >
                  <Fieldset
                    legend=""
                    css={
                      {
                        display: 'flex',
                        maxWidth: '436px',
                      } as GDSStyleObject
                    }
                  >
                    <Radio
                      id="patientGender-1"
                      name={form.patientGender}
                      checked={form.patientGender === 'M'}
                      value="M"
                      label="MALE"
                      onChange={onUpdateField}
                    />
                    <Radio
                      id="patientGender-2"
                      name={form.patientGender}
                      checked={form.patientGender === 'F'}
                      value="F"
                      label="FEMALE"
                      onChange={onUpdateField}
                    />
                  </Fieldset>
                </FormField>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </StackLayout>
  );
}
